import React from 'react';

import {
  faArrowTurnRight,
  faLink,
  faSparkles,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDispatch } from 'react-redux';

import { SearchCategory as SearchCategoryEnum } from '../../openapi/leo';
import { postKbarActionAnalytics } from '../../slices/LeoSlice';
import { AppDispatch } from '../../types';
import { cn } from '../../utils/classUtils';
import {
  ContextualPrompt,
  NeoLeoSearchResult,
  NeoLeoSearchResultItem,
} from '../../utils/LeoUtils';

interface NeoLeoKBarActionsProps {
  isOpen: boolean;
  actions: NeoLeoSearchResult[];
  onActionClick: (prompt: ContextualPrompt) => void;
  resetInput(): void;
  showActionsBelowInput?: boolean;
}

const NeoLeoKBarActions: React.FC<NeoLeoKBarActionsProps> = ({
  isOpen,
  actions,
  onActionClick,
  resetInput,
  showActionsBelowInput = false,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const renderActionIcon = (
    category: SearchCategoryEnum,
    item: NeoLeoSearchResultItem,
  ) => {
    switch (category) {
      case SearchCategoryEnum.Roar:
      case SearchCategoryEnum.PromptSuggestions:
      case SearchCategoryEnum.MarketingSignReview:
        return (
          <FontAwesomeIcon
            icon={faSparkles}
            className='text-reskin-aqua'
            fontSize={14}
          />
        );

      case SearchCategoryEnum.ExternalLinks:
        return (
          <FontAwesomeIcon
            icon={faLink}
            className='text-reskin-aqua'
            fontSize={14}
          />
        );

      case SearchCategoryEnum.InternalPages:
        return (
          <FontAwesomeIcon
            icon={faArrowTurnRight}
            className='text-reskin-aqua'
            fontSize={14}
          />
        );

      case SearchCategoryEnum.Deposits:
        return (
          <p className='font-inter-regular text-sm text-white pt-0.5'>
            {item?.title?.charAt(0)}
          </p>
        );
    }
  };

  const handleOnActionClick = (
    category: SearchCategoryEnum,
    item: NeoLeoSearchResultItem,
  ) => {
    resetInput();
    dispatch(
      postKbarActionAnalytics({ category: category, item: item as any }),
    );
    switch (category) {
      case SearchCategoryEnum.PromptSuggestions:
        onActionClick({
          prompt: item.prompt ?? '',
          title: item.title ?? item.prompt ?? '',
          category: null,
          prompt_type: item.prompt_type ?? undefined,
        });
        break;

      case SearchCategoryEnum.ExternalLinks:
        window.open(item.url!, '_blank');
        break;

      case SearchCategoryEnum.Deposits:
    }
  };

  return (
    <div
      className={cn(
        'absolute w-full rounded-lg max-h-[350px] overflow-y-auto',
        showActionsBelowInput ? 'top-12' : 'bottom-16',
      )}
    >
      {isOpen && (
        <div
          className='bg-reskin-regent-900 border border-white/[.16] pt-4'
          data-testid='kbar-actions'
        >
          {actions.map((action, index) => (
            <div key={[action.category, index].join('-')} className='pb-4'>
              <p className='font-inter-medium text-reskin-regent-600 px-4'>
                {action.category_title?.toUpperCase()}
              </p>
              {action.items.map((item, index) => (
                <div
                  key={[item.title, index].join('-')}
                  className='flex items-center justify-between cursor-pointer py-1.5 px-4 hover:bg-white/[0.14]'
                  data-testid='suggestion-item'
                  onClick={() => {
                    handleOnActionClick(action.category, item);
                  }}
                >
                  <div className='flex items-center gap-x-3'>
                    <div className='flex justify-center items-center w-8 h-6 bg-white bg-opacity-10 rounded-sm flex-shrink-0'>
                      {renderActionIcon(action.category, item)}
                    </div>
                    <div>
                      <p className='font-inter-light text-sm text-white pt-0.5'>
                        {item.title}
                      </p>
                    </div>
                    <div />
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NeoLeoKBarActions;
